<template>
  <div v-show="isVisible" :id="scopedItemId" :class="rootClasses" :data-name="name">
    <component :is="headerTag" :class="headWrapperClasses" :style="{top: isSticky ? `${stickyHeaderOffset}` : 'unset'}">
      <button
        :id="scopedHeadId"
        class="pdl-collapse-item__header font-medium"
        :class="headClasses"
        :tabindex="disabled ? -1 : 0"
        type="button"
        :aria-expanded="isActive"
        :qaid="qaidValue"
        :aria-controls="`pdl-collapse-content-${id}`"
        @click="handleHeaderClick"
        @keydown.space.enter.stop.prevent="handleKeydown"
        @focus="handleFocus"
        @blur="focusing = false"
      >
        <span class="flex items-center grow">
          <!-- @slot Slot for title. -->
          <slot name="title">{{ title }}</slot>
        </span>

        <!-- @slot Slot for icon. -->
        <slot name="icon">
          <pdl-icon
            v-if="!isDisabled"
            :name="arrowName"
            class="pdl-collapse-item__icon"
            :qaid="qaidChevronValue"
            :class="iconClasses"
          />
        </slot>
      </button>
    </component>

    <pdl-collapse-transition :is-enabled="isEnableTransition.value">
      <div
        v-show="isActive"
        :id="`pdl-collapse-content-${id}`"
        class="pdl-collapse-item__wrap"
        v-bind="shouldRenderAsRegion ? {role: 'region'} : {}"
        :aria-hidden="!isActive"
        :aria-labelledby="`pdl-collapse-head-${id}`"
      >
        <div ref="content" class="pdl-collapse-item__content" :class="contentClasses">
          <!-- @slot Slot for content. -->
          <slot></slot>
        </div>
      </div>
    </pdl-collapse-transition>
  </div>
</template>

<script setup>
import {ref, useSlots, onMounted} from 'vue';
import PdlCollapseTransition from './CollapseTransition.vue';
import {PdlIcon} from '@pedal/pdl-icon';
import {useCollapseItem, useCollapseItemDOM} from './useCollapseItem';
import {PdlSlotUtil} from '@pedal/pdl-shared-libraries';
import {v4 as uuidv4} from 'uuid';
import {STATE_CHANGED_EVENT} from './constants';
defineOptions({
  name: 'PdlCollapseItem',
});

const emit = defineEmits({
  [STATE_CHANGED_EVENT]: (value) => value,
});
const props = defineProps({
  /**
   * Text title of collapse panel.
   */
  title: {
    type: String,
    default: '',
  },
  /**
   * Qaid of collapse panel.
   */
  qaid: {
    type: String,
    default: '',
  },
  /**
   * Unique identification of collapse panel.
   */
  name: {
    type: [String, Number],
    default() {
      return uuidv4();
    },
  },
  /**
   * Enables transition css.
   */
  isEnableTransition: {
    type: Boolean,
    default: true,
  },
  /**
   * Disable the collapse panel.
   */
  disabled: {
    type: Boolean,
    default: false,
  },
});

const isDisabled = ref(props?.disabled);
const isVisible = ref(true);
const {
  focusing,
  id,
  isActive,
  handleFocus,
  handleHeaderClick,
  handleKeydown,
  isSticky,
  mountItem,
  shouldRenderAsRegion,
  headerTag,
} = useCollapseItem(props, emit);

const {
  arrowName,
  rootClasses,
  stickyHeaderOffset,
  headClasses,
  headWrapperClasses,
  contentClasses,
  iconClasses,
  scopedItemId,
  scopedHeadId,
  qaidChevronValue,
  qaidValue,
} = useCollapseItemDOM(props, {focusing, isActive, id});

const slots = useSlots();

onMounted(() => {
  if (!PdlSlotUtil.hasSlotContent(slots?.default)) isVisible.value = false;
  mountItem();
});

defineExpose({
  /** @description current collapse-item whether active */
  isActive,
});
</script>
<style lang="scss">
@import 'Collapse.scss';
</style>
